import { RateconRevisionInvoiceContext } from '@numeo/types-main-web-app'
import { Input, Select } from '@/components/ds2'
import {
    applyBillingType,
    BILLING_TYPE_SECTIONS,
    BillingDraft,
    BillingResolution,
    billingAmountText,
    billingKind,
    billingTypeValue,
    describeBillingOutcome,
    formatUsd,
} from '../utils/revisionAdjustments'

interface RateconRevisionBillingPanelProps {
    context: RateconRevisionInvoiceContext
    draft: BillingDraft
    onChange: (draft: BillingDraft) => void
    rateChanged: boolean
    /** The rate accepting would write: the revised one, or the dispatcher's edit-merged one. */
    effectiveRate: number
    resolution: BillingResolution
    disabled?: boolean
}

/** What accepting means once the load is invoiced. The invoice itself is never touched: the load takes the revised terms and the
 *  difference waits in Ready to Invoice, on the load it belongs to, until someone turns it into an invoice. */
export const RateconRevisionBillingPanel = ({
    context,
    draft,
    onChange,
    rateChanged,
    effectiveRate,
    resolution,
    disabled,
}: RateconRevisionBillingPanelProps) => {
    const kind = billingKind(draft, rateChanged)
    // What is still missing is a prompt until the dispatcher has touched the fields; only then is it an error.
    const touched = draft.amount !== null || draft.kind !== null

    return (
        <div data-testid="ratecon-revision-billing-panel" className="space-y-2">
            <div className="flex items-center justify-between gap-3 text-xs">
                <span className="min-w-0 truncate text-foreground-muted">
                    <span className="font-semibold text-foreground">{context.originalInvoiceNumber}</span>
                    {context.billToName ? ` · ${context.billToName}` : ''}
                </span>
                <span className="shrink-0 text-foreground-muted">
                    Line haul billed <span className="font-semibold text-foreground">{formatUsd(context.billedAmount)}</span>
                </span>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_8.5rem_minmax(0,1.3fr)]">
                <Select
                    label="Charge for"
                    value={billingTypeValue(kind, draft.accessorialType)}
                    placeholder="Choose…"
                    sections={BILLING_TYPE_SECTIONS}
                    onChange={(value) => onChange(applyBillingType(draft, value))}
                    disabled={disabled}
                    data-testid="billing-kind"
                />
                <Input
                    inputType="number"
                    label="Amount ($)"
                    value={billingAmountText(draft, context, rateChanged, effectiveRate)}
                    onChange={(e) => onChange({ ...draft, amount: e.target.value })}
                    disabled={disabled}
                    placeholder="0.00"
                    error={touched && !resolution.ok}
                    data-testid="billing-amount"
                />
                <Input
                    label="Description (optional)"
                    value={draft.note}
                    onChange={(e) => onChange({ ...draft, note: e.target.value })}
                    disabled={disabled}
                    placeholder="e.g. 2 h at the shipper"
                    data-testid="billing-note"
                />
            </div>

            <p
                className={`text-xs leading-4 ${resolution.ok || !touched ? 'text-foreground-muted' : 'text-danger'}`}
                data-testid="billing-outcome"
                role={resolution.ok || !touched ? undefined : 'alert'}
            >
                {describeBillingOutcome(context, resolution, rateChanged, effectiveRate)}
                {context.openAdjustment && resolution.ok && (
                    <span data-testid="billing-open-adjustment"> It replaces the {formatUsd(context.openAdjustment.amount)} already waiting on this load.</span>
                )}
            </p>
        </div>
    )
}
